import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search } from 'lucide-react';
import { useUserContext } from '../../../contexts/UserContext';
import { AgTableCard, Input } from '../../../components/UI';
import { getAdobeTargetActivitiesRevisionsData } from '../../../utils/APIs';
import { CSVLink } from 'react-csv';
import DateRangePicker from '../../../components/UI/DateRangePicker';
import dayjs from 'dayjs';

// ─── Expanded detail row renderer ──────────────────────────────────────────────
// Shows per-user totals: user email / id + total count + first 5 revision IDs
const RevisionsDetailRenderer = ({ data, allOperations }) => {
    const users = data?.users || [];

    // Use parent's allOperations to ensure consistency, fallback to local if not provided
    const allOps = allOperations || Array.from(
        new Set(users.flatMap(u => Object.keys(u.operationCounts || {})))
    ).sort();

    return (
        <div className="px-10 py-3 bg-gray-50 border-b border-gray-200 h-full overflow-y-auto">
            <table className="w-full text-sm">
                <thead>
                    <tr className="text-left text-gray-500 border-b border-gray-200">
                        <th className="pb-2 font-medium pr-8">User Email</th>
                        <th className="pb-2 font-medium pr-8">User Name</th>
                        {allOps.map(op => (
                            <th key={op} className="pb-2 font-medium pr-6 capitalize">{op}</th>
                        ))}
                        {/* <th className="pb-2 font-medium">Sample Revision IDs</th> */}
                    </tr>
                </thead>
                <tbody>
                    {users.map((user, i) => (
                        <tr key={i} className="border-b border-gray-100 last:border-0">
                            <td className="py-2 pr-8 text-gray-500">{user.email || <span className="text-gray-300">—</span>}</td>
                            <td className="py-2 pr-8 text-gray-800">{user.name || user.user_id}</td>
                            {allOps.map(op => (
                                <td key={op} className="py-2 pr-6 text-gray-600">
                                    {(user.operationCounts?.[op] ?? 0).toLocaleString()}
                                </td>
                            ))}
                            {/* <td className="py-2 text-gray-500 text-xs truncate max-w-xs">
                                {user.revisionIds?.slice(0, 5).join(', ')}
                                {user.revisionIds?.length > 5 && ` … (+${user.revisionIds.length - 5})`}
                            </td> */}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};


const AdobeTargetActivitiesRevisionsData = () => {
    const navigate = useNavigate();
    const { userData } = useUserContext();

    const [rowData, setRowData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [expandedRows, setExpandedRows] = useState(new Set());
    const [searchText, setSearchText] = useState('');
    const [gridApi, setGridApi] = useState(null);
    const [startDate, setStartDate] = useState(dayjs().subtract(1, 'month').format('YYYY-MM-DD'));
    const [endDate, setEndDate] = useState(dayjs().format('YYYY-MM-DD'));
    const [gridWidth, setGridWidth] = useState('100%');
    const resizeObserverRef = useRef(null);

    const gridContainerRef = useCallback(node => {
        if (resizeObserverRef.current) {
            resizeObserverRef.current.disconnect();
        }
        if (node) {
            resizeObserverRef.current = new ResizeObserver(entries => {
                for (let entry of entries) {
                    setGridWidth(entry.contentRect.width);
                }
            });
            resizeObserverRef.current.observe(node);
        }
    }, []);

    // Collect all unique operation types across all rows for column headers, ensuring base operations always exist
    const allOperations = useMemo(() => {
        const ops = new Set(['create', 'update', 'delete']);
        rowData.forEach(row => {
            Object.keys(row.operationCounts || {}).forEach(op => ops.add(op));
        });
        return Array.from(ops).sort();
    }, [rowData]);

    useEffect(() => {
        fetchData(startDate, endDate);
    }, []);

    const fetchData = async (start = startDate, end = endDate) => {
        try {
            setLoading(true);
            setError('');
            const result = await getAdobeTargetActivitiesRevisionsData(
                userData?.token,
                userData?.selectedOrganization?.org_token,
                start,
                end
            );
            setRowData(result?.data || []);
        } catch (err) {
            setError(err?.message || 'Failed to load data. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const toggleRow = useCallback((activityId) => {
        setExpandedRows(prev => {
            const next = new Set(prev);
            if (next.has(activityId)) next.delete(activityId);
            else next.add(activityId);
            return next;
        });
        setTimeout(() => {
            if (gridApi) {
                gridApi.resetRowHeights();
            }
        }, 0);
    }, [gridApi]);

    const getRowHeight = useCallback((params) => {
        if (expandedRows.has(params.data?.activity_id)) {
            return Math.min((params.data.users?.length || 0) * 36 + 60, 480) + 48; // 48 is base row height
        }
        return 48;
    }, [expandedRows]);

    const columns = useMemo(() => {
        const activityCol = {
            headerName: 'Activity Name',
            field: 'activity_name',
            sortable: true,
            filter: true,
            resizable: true,
            flex: 1,
            minWidth: 300,
            cellStyle: (params) => {
                if (expandedRows.has(params.data?.activity_id)) {
                    return { overflow: 'visible', zIndex: 999 };
                }
                return {};
            },
            cellRenderer: (params) => {
                const hasUsers = params.data?.users?.length > 0;
                const isExpanded = expandedRows.has(params.data?.activity_id);
                const handleClick = hasUsers ? () => {
                    toggleRow(params.data?.activity_id);
                } : undefined;
                
                return (
                    <div className="h-full w-full relative">
                        <div className={`flex items-center gap-2 h-[48px] ${hasUsers ? 'cursor-pointer text-primary hover:underline' : ''}`} onClick={handleClick}>
                            {hasUsers && (
                                <svg className={`w-4 h-4 shrink-0 transition-transform ${isExpanded ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </svg>
                            )}
                            <span>{params.data?.activity_name || params.data?.activity_id}</span>
                        </div>
                        {isExpanded && (
                            <div 
                                className="absolute top-[48px] bg-white shadow-lg border border-gray-200 rounded-b-lg"
                                style={{ left: '-17px', width: gridWidth, height: 'calc(100% - 48px)', overflowY: 'auto' }}
                            >
                                <RevisionsDetailRenderer data={params.data} allOperations={allOperations} />
                            </div>
                        )}
                    </div>
                );
            },
        };

        const totalCol = {
            headerName: 'Total Revisions',
            field: 'total',
            sortable: true,
            filter: true,
            resizable: true,
            width: 150,
            valueFormatter: (p) => p.value != null ? p.value.toLocaleString() : '',
        };

        const opCols = allOperations.map(op => ({
            headerName: op.charAt(0).toUpperCase() + op.slice(1),
            field: `op_${op}`,
            sortable: true,
            filter: true,
            resizable: true,
            width: 130,
            valueGetter: (params) => params.data?.operationCounts?.[op] ?? 0,
            valueFormatter: (p) => p.value != null ? p.value.toLocaleString() : '0',
        }));

        const uniqueUsersCol = {
            headerName: 'Unique Users',
            field: 'uniqueUsers',
            sortable: true,
            filter: true,
            resizable: true,
            width: 160,
            valueGetter: (params) => params.data?.users?.length ?? 0,
        };

        return [activityCol, totalCol, ...opCols, uniqueUsersCol];
    }, [allOperations, expandedRows, toggleRow]);

    const csvData = useMemo(() => {
        const rows = [];
        rowData.forEach(activity => {
            // Top-level Activity Row
            const activityRow = {
                'Activity Name': activity.activity_name || activity.activity_id,
                'Activity ID': activity.activity_id,
                'User Email': '',
                'User Name': '',
                'Total Revisions': activity.total,
                'Unique Users': activity.users?.length ?? 0,
            };
            allOperations.forEach(op => {
                const opName = op.charAt(0).toUpperCase() + op.slice(1);
                activityRow[opName] = activity.operationCounts?.[op] ?? 0;
            });
            rows.push(activityRow);

            // Child User Rows
            if (activity.users && activity.users.length > 0) {
                activity.users.forEach(user => {
                    const userRow = {
                        'Activity Name': '',
                        'Activity ID': '',
                        'User Email': user.email || '—',
                        'User Name': user.name || user.user_id,
                        'Total Revisions': '',
                        'Unique Users': '',
                    };
                    allOperations.forEach(op => {
                        const opName = op.charAt(0).toUpperCase() + op.slice(1);
                        userRow[opName] = user.operationCounts?.[op] ?? 0;
                    });
                    rows.push(userRow);
                });
            }
        });
        return rows;
    }, [rowData, allOperations]);

    const autoSizeStrategy = useMemo(() => ({
        type: window.innerWidth < 1024 ? 'fitCellContents' : 'fitGridWidth',
        defaultMinWidth: 100,
    }), []);

    const onGridReady = (params) => setGridApi(params.api);

    const handleSearch = (e) => {
        setSearchText(e.target.value);
        gridApi?.setQuickFilter(e.target.value);
    };

    return (
        <main id="main" className="flex flex-col w-full space-y-4 my-6 px-4 md:px-8">
            {/* Header */}
            <div className="flex flex-col gap-1">
                <button
                    onClick={() => navigate(-1)}
                    className="font-medium text-primary hover:underline flex items-center w-fit mb-2"
                >
                    <svg className="w-4 h-4 mr-1" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 14 10">
                        <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 5H1m0 0 4 4M1 5l4-4" />
                    </svg>
                    Back to saved tasks
                </button>
                <h1 className="text-xl font-medium leading-tight tracking-tight text-gray-900 md:text-2xl">
                    Adobe Target Activity Revisions
                </h1>
                <p className="text-gray-500 text-sm">
                    Per-activity revision history aggregated across all saved task runs.
                    Click on an activity row to expand user-level breakdown.
                </p>
            </div>

            {/* Error */}
            {error && <p className="text-red-500 text-sm">{error}</p>}

            <AgTableCard
                header={
                    <div className="flex items-end justify-between gap-3 flex-wrap w-full">
                        <div className="w-full md:w-80">
                            <Input
                                value={searchText}
                                onChange={handleSearch}
                                prefixIcon={<Search className="w-4 h-4 text-stride-muted" />}
                                placeholder="Search activity..."
                                clearable
                            />
                        </div>
                        <div className="flex items-end gap-2 flex-wrap">
                            <button
                                title="Refresh"
                                onClick={() => fetchData(startDate, endDate)}
                                className="p-2.5 text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-50"
                            >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                </svg>
                            </button>
                            {rowData.length > 0 && (
                                <CSVLink
                                    data={csvData}
                                    filename={`at-revisions-${new Date().getTime()}.csv`}
                                >
                                    <button title="Download CSV" className="p-2.5 text-white bg-primary border border-primary rounded-lg hover:opacity-90">
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                        </svg>
                                    </button>
                                </CSVLink>
                            )}
                            <DateRangePicker
                                startDate={startDate}
                                endDate={endDate}
                                onDateChange={(start, end) => {
                                    setStartDate(start);
                                    setEndDate(end);
                                    fetchData(start, end);
                                }}
                                label="Filter by date"
                                allowFuture={false}
                            />
                        </div>
                    </div>
                }
                cardRef={gridContainerRef}
                tableId="adobe-target-revisions-table"
                loading={loading}
                emptyMessage="No revision data found. Run the task first to populate data."
                rowData={rowData}
                columnDefs={columns}
                defaultColDef={{ sortable: true, filter: true, resizable: true }}
                autoSizeStrategy={autoSizeStrategy}
                onGridReady={onGridReady}
                getRowHeight={getRowHeight}
            />
        </main>
    );
};

export default AdobeTargetActivitiesRevisionsData;
